import { TEST_IDS } from '@va/constants';
import { useAddNotification } from '@va/dashboard/util-hooks';
import { Spinner } from '@va/icons';
import { useTranslate } from '@va/localization';
import {
  CustomDomainPlatformType,
  CustomDomainsApi,
  CustomDomainTarget,
  CustomDomainType,
} from '@va/shared/api/custom-domains';
import { useGetTargetedDNSRecords } from '@va/shared/api/custom-domains/custom-domains.api.hooks';
import { DNSVerificationStatusMap, VerifyDnsSection } from '@va/shared/custom-domains';
import { Button, GrayGradientDivider, ModalHeader, Paragraph } from '@va/ui/design-system';
import { ModalWrapper } from '@va/util/components';
import { useCallback, useState } from 'react';

export const CustomSupportCenterDomainCreationModal = ({
  isOpen,
  closeModal,
  domain,
  onSuccess,
  customDomainId,
  companyId,
}: {
  isOpen: boolean;
  customDomainId: number;
  domain: string;
  closeModal: () => void;
  onSuccess: () => void;
  companyId: string;
}) => {
  const [dnsVerificationStatusMap, setDnsVerificationStatusMap] = useState<DNSVerificationStatusMap>({});
  const [isLoadingDNSCheck, setIsLoadingDNSCheck] = useState(false);
  const translate = useTranslate();
  const [dnsCheckSucceeded, setDnsCheckSucceeded] = useState(false);
  const [isLoadingDomainSave, setIsLoadingDomainSave] = useState(false);

  const dnsRecordsReq = useGetTargetedDNSRecords(
    !domain
      ? null
      : {
          platform: CustomDomainPlatformType.AWL,
          domain: domain,
          target: CustomDomainTarget.supportCenter,
        },
  );
  const { showErrorNotification } = useAddNotification();

  const saveCustomDomain = useCallback(async () => {
    setIsLoadingDomainSave(true);
    try {
      if (customDomainId) {
        await CustomDomainsApi.activateCompanyDomain({
          companyId: companyId,
          customDomainId: customDomainId,
        });
      } else {
        await CustomDomainsApi.createCompanyDomain({
          companyId: companyId,
          domain: domain,
          platform: CustomDomainPlatformType.AWL,
          type: CustomDomainType.supportCenter,
        });
      }

      closeModal();
      onSuccess?.();
    } catch (error) {
      console.error(error);
      showErrorNotification(undefined, CustomDomainsApi.extractApiErrorMessage(error, translate));
    } finally {
      setIsLoadingDomainSave(false);
    }
  }, [closeModal, companyId, customDomainId, domain, onSuccess, showErrorNotification, translate]);

  return (
    <ModalWrapper closeModal={closeModal} isModalOpen={isOpen}>
      <div className='max-w-[46.875rem] w-screen'>
        <ModalHeader
          title={translate('standalone.customDomains.customSupportCenter.confirmDomainChange.title')}
          onClose={closeModal}
          className={'overflow-hidden'}
          titleClassName={'truncate'}
        />

        <div className='p-4 space-y-3'>
          <Paragraph>{translate('standalone.customDomains.customSupportCenter.confirmDomainChange.line1')}</Paragraph>

          <VerifyDnsSection
            expectedRecordsCount={2}
            domain={domain}
            dnsRecords={dnsRecordsReq.data}
            isLoading={isLoadingDNSCheck}
            setIsLoading={setIsLoadingDNSCheck}
            dnsVerificationStatusMap={dnsVerificationStatusMap}
            setDnsVerificationStatusMap={setDnsVerificationStatusMap}
            buttonColor={dnsCheckSucceeded ? 'quaternary' : 'primary'}
            onSuccess={() => {
              setDnsCheckSucceeded(true);
            }}
            onFailure={() => {
              setDnsCheckSucceeded(false);
            }}
          />
        </div>

        <GrayGradientDivider />

        <div className='flex flex-row justify-end p-3 gap-3'>
          <Button
            className='rounded-12 flex-1'
            color='tertiary'
            text={translate('button.cancel')}
            onClick={closeModal}
            data-testid={TEST_IDS.generic.buttons.cancel}
          />
          <Button
            className='rounded-12 flex-2 '
            color='primary'
            icon={isLoadingDomainSave ? () => <Spinner /> : undefined}
            disabled={!dnsCheckSucceeded || isLoadingDomainSave}
            text={translate('button.confirm')}
            onClick={saveCustomDomain}
            data-testid={TEST_IDS.generic.buttons.apply}
          />
        </div>
      </div>
    </ModalWrapper>
  );
};
